import { benchSeatTopMm } from "./benchHeight";
import { resolveBenchSupport } from "./benchSupport";
import { resolveBenchSkirt } from "./benchSkirt";
import { resolveBenchBackrest } from "./benchBackrest";
import {
	BENCH_FRAME_HEIGHT_MM,
	BENCH_FRAME_THICKNESS_MM,
	BENCH_LEDGER_HEIGHT_MM,
	BENCH_LEG_ALONG_MM,
	BENCH_LEG_ACROSS_MM,
	BENCH_SLAT_THICKNESS_MM,
	BENCH_SLAT_WIDTH_MM,
	BENCH_SLAT_GAP_MM,
	benchLegCount,
} from "./constants";
import {
	benchRunFootprint,
	footprintFromWallSpans,
	footprintsOverlap,
	wallRunsAlongX,
	type BenchRun,
	type InteriorRoom,
	type FootprintMm,
} from "./types";

export interface BenchVolume extends FootprintMm {
	minYMm: number;
	maxYMm: number;
}

/** Assembly envelopes in finished-floor coordinates. Seat/frame bands are
 * conservative; legs, cleats and skirt boards retain their individual footprints
 * so a lower seat can pass beneath a wall-mounted upper seat. */
export function benchAssemblyVolumes(
	run: BenchRun,
	room: InteriorRoom,
): BenchVolume[] {
	const fp = benchRunFootprint(run, room);
	const alongX = wallRunsAlongX(run.wall);
	const u0 = alongX ? fp.minXMm : fp.minZMm;
	const u1 = alongX ? fp.maxXMm : fp.maxZMm;
	const v0 = alongX ? fp.minZMm : fp.minXMm;
	const v1 = alongX ? fp.maxZMm : fp.maxXMm;
	const wallAtLow = run.wall === "back" || run.wall === "left";
	const backV = wallAtLow ? v0 : v1;
	const frontV = wallAtLow ? v1 : v0;
	const towardWall = wallAtLow ? -1 : 1;
	const top = benchSeatTopMm(run, room.wallHeightMm);
	const under = top - BENCH_SLAT_THICKNESS_MM;
	const { support, legHeightMm } = resolveBenchSupport(run, room);
	const skirt = resolveBenchSkirt(run, room);
	const boxes: BenchVolume[] = [
		{ ...fp, minYMm: under - BENCH_FRAME_HEIGHT_MM, maxYMm: top },
	];
	const box = (
		ua: number,
		ub: number,
		va: number,
		vb: number,
		ya: number,
		yb: number,
	) => {
		boxes.push({
			...footprintFromWallSpans(
				run.wall,
				{ min: Math.min(ua, ub), max: Math.max(ua, ub) },
				{ min: Math.min(va, vb), max: Math.max(va, vb) },
			),
			minYMm: ya,
			maxYMm: yb,
		});
	};
	if (support === "wall" || support === "wall+frontLegs") {
		box(
			u0 + BENCH_FRAME_THICKNESS_MM,
			u1 - BENCH_FRAME_THICKNESS_MM,
			backV,
			backV - towardWall * BENCH_FRAME_THICKNESS_MM,
			under - BENCH_LEDGER_HEIGHT_MM,
			under,
		);
	}
	if (support === "endWalls") {
		for (const u of [u0, u1 - BENCH_FRAME_THICKNESS_MM])
			box(
				u,
				u + BENCH_FRAME_THICKNESS_MM,
				v0,
				v1,
				under - BENCH_FRAME_HEIGHT_MM - BENCH_LEDGER_HEIGHT_MM,
				under - BENCH_FRAME_HEIGHT_MM,
			);
	}
	if (legHeightMm > 0) {
		const front =
			frontV +
			towardWall *
				(BENCH_FRAME_THICKNESS_MM / 2 + (skirt ? BENCH_SLAT_THICKNESS_MM : 0));
		const back = backV - (towardWall * BENCH_FRAME_THICKNESS_MM) / 2;
		const inset = BENCH_LEG_ALONG_MM / 2 + 30;
		const count = benchLegCount(run.lengthMm);
		for (const v of support === "floor" ? [front, back] : [front]) {
			for (let i = 0; i < count; i++) {
				const u = u0 + inset + (i / (count - 1)) * (run.lengthMm - 2 * inset);
				box(
					u - BENCH_LEG_ALONG_MM / 2,
					u + BENCH_LEG_ALONG_MM / 2,
					v - BENCH_LEG_ACROSS_MM / 2,
					v + BENCH_LEG_ACROSS_MM / 2,
					under - legHeightMm,
					under,
				);
			}
		}
	}
	if (skirt) {
		for (let i = 0; i < skirt.boardCount; i++) {
			const y = skirt.topMm - i * (BENCH_SLAT_WIDTH_MM + BENCH_SLAT_GAP_MM);
			box(
				u0,
				u1,
				frontV,
				frontV + towardWall * BENCH_SLAT_THICKNESS_MM,
				y - BENCH_SLAT_WIDTH_MM,
				y,
			);
			for (const u of [
				...(skirt.openAtStart ? [u0] : []),
				...(skirt.openAtEnd ? [u1 - BENCH_SLAT_THICKNESS_MM] : []),
			])
				box(
					u,
					u + BENCH_SLAT_THICKNESS_MM,
					frontV + towardWall * BENCH_SLAT_THICKNESS_MM,
					frontV + towardWall * (BENCH_SLAT_THICKNESS_MM + skirt.endLengthMm),
					y - BENCH_SLAT_WIDTH_MM,
					y,
				);
		}
	}
	const backrest = resolveBenchBackrest(run, room);
	if (backrest) boxes.push(...backrest.boards, ...backrest.spacers);
	return boxes;
}

export function benchAssembliesOverlap(
	a: BenchRun,
	b: BenchRun,
	room: InteriorRoom,
): boolean {
	const bVolumes = benchAssemblyVolumes(b, room);
	return benchAssemblyVolumes(a, room).some((av) =>
		bVolumes.some(
			(bv) =>
				footprintsOverlap(av, bv) &&
				av.minYMm < bv.maxYMm - 0.5 &&
				bv.minYMm < av.maxYMm - 0.5,
		),
	);
}

/** Rectangle union, so overlapping tiers reserve floor only once. */
export function occupiedFloorAreaSqm(boxes: FootprintMm[]): number {
	const xs = [...new Set(boxes.flatMap((b) => [b.minXMm, b.maxXMm]))].sort(
		(a, b) => a - b,
	);
	let area = 0;
	for (let i = 1; i < xs.length; i++) {
		const intervals = boxes
			.filter((b) => b.minXMm < xs[i] && b.maxXMm > xs[i - 1])
			.map((b) => [b.minZMm, b.maxZMm])
			.sort((a, b) => a[0] - b[0]);
		let start = intervals[0]?.[0] ?? 0,
			end = start,
			length = 0;
		for (const [lo, hi] of intervals) {
			if (lo > end) {
				length += end - start;
				start = lo;
				end = hi;
			} else {
				start = Math.min(start, lo);
				end = Math.max(end, hi);
			}
		}
		area += (xs[i] - xs[i - 1]) * (length + end - start);
	}
	return area / 1_000_000;
}
